import { FloatingInput } from '@piplapps/pipl-ui';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import ApiService from 'Services/ApiService';
import TranslationUtils, { getText } from 'Utils/TranslationUtils';
import { Helmet } from 'react-helmet';
import { AppSelectors } from '../../../Redux/modules/app/selectors';
import styles from './SetPasswordPage.module.less';
import greenV from '../../../Assets/greenv.svg';
import AnalyticsHandler from '../../../Utils/AnalyticsHandler/AnalyticsHandler';
import AnalyticsEventNames from '../../../Utils/AnalyticsHandler/AnalyticsEventNames';
import AnalyticsCore from '../../../Utils/AnalyticsHandler/AnalyticsCore';
import { ProfileActions } from '../../../Redux/modules/profile/actions';
import { ProfileSelectors } from '../../../Redux/modules/profile/selectors';
import AppConfig from '../../../Config/AppConfig';
import { OrganizationSelectors } from '../../../Redux/modules/organization/selectors';

function SetPasswordPage(props) {
  const { currentEmail, expiredPassword } = props;
  const [error, setError] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState(null);

  const validate = (values) => {
    const errors = {};
    if (!values.password) {
      errors.password = TranslationUtils.getText('SET_PASSWORD_FIELD_REQUIRED');
    }

    if (!values.rPassword) {
      errors.rPassword = TranslationUtils.getText('SET_PASSWORD_FIELD_REQUIRED');
    }

    if (values.rPassword !== values.password) {
      errors.rPassword = TranslationUtils.getText('SET_PASSWORD_FIELD_PASSWORD_NOT_MATCH');
    }
    if (passwordError) {
      errors.password = getText(passwordError);
    }

    return errors;
  };
  const formik = useFormik({
    initialValues: {
      oldPassword: '',
      password: '',
      rPassword: '',
    },
    validate,
    onSubmit: async (values) => {
      setLoading(true);
      if (expiredPassword) {
        props.changePassword({ oldPassword: values.oldPassword, newPassword: values.password });
      } else {
        props.setPassword(values.password);
      }
      setError(error);
      setLoading(false);
      const event = AnalyticsCore.buildEvent({
        eventName: AnalyticsEventNames.SET_NEW_PASSWORD,
        tracking: props.tracking,
        user: props.user,
      });
      AnalyticsCore.sendEvent(event);
    },
  });
  useEffect(() => {
    if (props.passwordUpdateStatus === false) {
      formik.setFieldError('oldPassword', 'This is not your password');
    }
  }, [props.passwordUpdateStatus]);
  const title = expiredPassword ? gettext('Set a new password') : gettext('Set your password');
  async function checkPassword(password) {
    try {
      setPasswordError('evaluating password..');
      await ApiService.checkPassword({ password });
      setPasswordError(null);
      formik.setFieldError('password', null);
    } catch (e) {
      setPasswordError(e.response.data.error);
      formik.setFieldError('password', getText(e.response.data.error));
      const event = AnalyticsCore.buildEvent({
        eventName: AnalyticsEventNames.GOT_ERROR_SETTING_PASSWORD,
        eventLabel: e.response.data.error,
        tracking: props.tracking,
        user: props.user,
      });
      AnalyticsCore.sendEvent(event);
    }

    return true;
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      {!expiredPassword && (
        <div className={styles.thankYouTitle}>
          <span>{TranslationUtils.getText('Thank you for verifying your email:')} </span>
          {currentEmail && (
            <div className={styles.emailWrapper}>
              <span className={styles.email}>{currentEmail}</span>
              <img src={greenV} alt="" />
            </div>
          )}
        </div>
      )}

      <div className={[styles.formWrapper, expiredPassword ? null : styles.border].join(' ')}>
        <div className={styles.formTitle}>
          <span>{title} </span>
        </div>
        {props.strongPasswordPolicy && (
          <div className={styles.paragraph}>
            <div className={styles.paragraphLine}>
              <span className={styles.dot}>•</span>
              <span>
                {gettext('Use at least 8 characters, a combination of numbers, letters, and special characters.')}
              </span>
            </div>
            <div className={styles.paragraphLine}>
              <span className={styles.dot}>•</span>
              <span>{gettext('Do not use your previous passwords.')}</span>
            </div>
            <div className={styles.paragraphLine}>
              <span className={styles.dot}>•</span>
              <span>
                {gettext(
                  'Do not use your name, email address, mobile number, or any other information that might be accessed easily.',
                )}
              </span>
            </div>
            <div className={styles.paragraphLine}>
              <span className={styles.dot}>•</span>
              <span>{gettext('Do not use the same password for more than one online account.')}</span>
            </div>
          </div>
        )}

        <div className={styles.formFields}>
          <form onSubmit={formik.handleSubmit} id="set-password-form">
            {expiredPassword && (
              <>
                <div className={styles.fieldWrapper}>
                  <FloatingInput
                    name="oldPassword"
                    errors={formik.errors}
                    touched={formik.touched}
                    onBlur={formik.handleBlur}
                    error={formik.errors.oldPassword}
                    onChange={formik.handleChange}
                    value={formik.values.oldPassword}
                    type="password"
                    placeholder={gettext('Old password')}
                  />
                </div>
              </>
            )}
            <div className={styles.fieldWrapper}>
              <FloatingInput
                name="password"
                errors={formik.errors}
                touched={formik.touched}
                onBlur={async (e) => {
                  formik.handleBlur(e);
                  await checkPassword(formik.values.password);
                }}
                error={formik.errors.password}
                onChange={formik.handleChange}
                value={formik.values.password}
                type="password"
                placeholder={
                  expiredPassword ? gettext('New password') : TranslationUtils.getText('SET_PASSWORD_FIELD_PASSWORD')
                }
              />
            </div>
            <div className={styles.fieldWrapper}>
              <FloatingInput
                name="rPassword"
                errors={formik.errors}
                onBlur={formik.handleBlur}
                touched={formik.touched}
                onChange={formik.handleChange}
                value={formik.values.rPassword}
                type="password"
                placeholder={
                  expiredPassword
                    ? gettext('Confirm new password')
                    : TranslationUtils.getText('SET_PASSWORD_FIELD_REPEAT_PASSWORD')
                }
              />
            </div>
            <div className={styles.fieldWrapper}>
              <AnalyticsHandler eventName={AnalyticsEventNames.SET_NEW_PASSWORD}>
                <button
                  test-id="submit-button"
                  className={`${styles.submitButton}`}
                  type="submit"
                  // disabled={!isValid || isLoading}
                >
                  {TranslationUtils.getText('Save password')}
                  {isLoading && (
                    <span className={styles.spinnerWrapper}>
                      <span className="glyphicon-wrapper">
                        <span className="glyphicon glyphicon-refresh glyphicon-refresh-animate" />
                      </span>
                    </span>
                  )}
                </button>
              </AnalyticsHandler>
            </div>
          </form>
          <div className={styles.forgetPassword}>
            {expiredPassword && <a href={`/${AppConfig.ACCOUNTS_PREFIX}logged-forgot-password/`}>Forgot password?</a>}
          </div>
        </div>
      </div>
    </>
  );
}

SetPasswordPage.propTypes = {
  currentEmail: PropTypes.string,
  expiredPassword: PropTypes.bool,
  changePassword: PropTypes.func,
  setPassword: PropTypes.func,
  passwordUpdateStatus: PropTypes.string,
  strongPasswordPolicy: PropTypes.bool,
  tracking: PropTypes.object,
  user: PropTypes.object,
};

const mapStateToProps = (state) => {
  return {
    currentEmail: AppSelectors.onBoardingCurrentEmailSelector(state),
    passwordUpdateStatus: ProfileSelectors.passwordUpdateStatusSelector(state),
    strongPasswordPolicy: OrganizationSelectors.strongPasswordPolicySelector(state),
    tracking: AppSelectors.trackingSelector(state),
    user: AppSelectors.userSelector(state),
  };
};

const mapDispatchToProps = {
  setPassword: ProfileActions.setPassword,
  changePassword: ProfileActions.changePassword,
};

export default connect(mapStateToProps, mapDispatchToProps)(SetPasswordPage);
